import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import * as currency from 'currency-formatter';
import type { Control, FieldValues } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { CopyText } from '@/components/ui/copy-text';
import { SortableHeader } from '@/components/ui/data-table';
import { Field, FieldLabel } from '@/components/ui/field';
import { FormAsyncSelect } from '@/components/ui/form-fields/form-async-select';
import { FormCurrencyInput } from '@/components/ui/form-fields/form-currency-input';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { FormMaskedInput } from '@/components/ui/form-fields/form-mask-input';
import { FormSelect } from '@/components/ui/form-fields/form-select';
import { FormSwitch } from '@/components/ui/form-fields/form-switch';
import { FormUploader } from '@/components/ui/form-fields/form-uploader';
import { MultiSelect } from '@/components/ui/multi-select';
import type { Filter } from '@/components/ui/search-toolbar';
import type { FieldDef, FilterDef, ColumnDef as ScreenColumnDef } from '@/db/schema';
import { apiClient } from '@/lib/api-client';
import { date, localeFormat } from '@/lib/date';
import { applyBestMask } from '@/lib/mask';
import { cellComponentRegistry, fieldComponentRegistry } from './component-registry';

type RelationRef = { resource: string; displayField: string };

/**
 * Hook de busca (com search-as-you-type) pra um recurso qualquer, no shape que
 * `AsyncSelect`/`FormAsyncSelect`/`SearchToolbar` esperam em `useQuery`/`selectOptions.useQuery`.
 */
function makeRelationSearchHook(resource: string) {
  return (params?: { search?: string }) =>
    useQuery({
      queryKey: ['relation-search', resource, params?.search],
      queryFn: async () => {
        const res = await (apiClient as any)[resource].get({
          query: { search: params?.search, perPage: 50 },
        });
        return (res.data?.data ?? []) as any[];
      },
    });
}

/** Lê a relação N:N de um registro via a rota aninhada `GET /:resource/:id/:relatedResource`. */
function useNestedRelation(
  resource: string,
  recordId: string | undefined,
  relatedResource: string,
) {
  return useQuery({
    queryKey: ['nested-relation', resource, recordId, relatedResource],
    queryFn: async () => {
      const res = await (apiClient as any)
        [resource]({ id: recordId })
        [relatedResource].get();
      return (res.data ?? []) as any[];
    },
    enabled: !!recordId,
  });
}

export function layoutToColumns(
  columns: ScreenColumnDef[],
  resource: string,
): ColumnDef<any>[] {
  return columns.map((col) => ({
    accessorKey: col.name,
    header: ({ column }: any) =>
      col.sortable === false ? (
        col.label
      ) : (
        <SortableHeader column={column} label={col.label} />
      ),
    cell: ({ row }: any) => {
      if (col.type === 'multi-relation' && col.relation?.resource) {
        return (
          <RelationBadges
            resource={resource}
            recordId={row.original.id}
            relation={col.relation}
          />
        );
      }

      const value = row.getValue(col.name);

      if (col.type === 'component' && col.component) {
        const Component = cellComponentRegistry[col.component];
        return Component ? <Component value={value} row={row.original} /> : value;
      }

      if (col.name === 'id' && typeof value === 'string') {
        return <CopyText value={value} label={`${value.slice(0, 8)}...`} />;
      }

      if (col.mask && col.mask.length > 0) {
        return value ? applyBestMask(String(value), col.mask) : value;
      }

      if (col.type === 'boolean') {
        return (
          <Badge variant={value ? 'success' : 'destructive'}>
            {value ? 'Sim' : 'Não'}
          </Badge>
        );
      }

      if (col.type === 'enum' && col.options) {
        return col.options.find((option) => option.value === value)?.label ?? value;
      }

      if (col.type === 'date' || col.type === 'datetime') {
        return value ? date(value).format(localeFormat) : '';
      }

      if (col.type === 'currency') {
        return currency.format(value ?? 0, { code: 'BRL' });
      }

      return value;
    },
  }));
}

function RelationBadges({
  resource,
  recordId,
  relation,
}: {
  resource: string;
  recordId: string;
  relation: RelationRef;
}) {
  const { data, isLoading } = useNestedRelation(resource, recordId, relation.resource);

  if (isLoading) return <span className="text-muted-foreground text-xs">...</span>;
  if (!data?.length) return <span className="text-muted-foreground text-xs">—</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {data.map((item) => (
        <Badge key={item.id} variant="secondary">
          {item[relation.displayField]}
        </Badge>
      ))}
    </div>
  );
}

export function layoutToFilters(filters: FilterDef[]): Filter<any>[] {
  return filters.map((f) => {
    if (f.relation?.resource) {
      const relation = f.relation;

      return {
        type: f.type,
        label: f.label,
        name: f.name,
        selectOptions: {
          useQuery: makeRelationSearchHook(relation.resource),
          getOptionValue: (item: any) => item.id,
          getDisplayValue: (item: any) => item[relation.displayField],
        },
      };
    }

    return {
      type: f.type,
      label: f.label,
      name: f.name,
      options: f.options,
    };
  });
}

interface ScreenFormProps {
  fields: FieldDef[];
  control: Control<FieldValues>;
  mode: 'create' | 'edit' | 'view';
  /** Necessários só pra campos `type: 'multi-relation'` (leem/gravam via rota aninhada). */
  resource?: string;
  recordId?: string;
}

/**
 * Renderiza os campos de uma screen a partir do layout salvo no banco.
 * Estado "view" (somente leitura) é resolvido pelo form-level `useForm({ disabled: true })`
 * do consumidor, não aqui — só `disabledOnEdit` é tratado como caso especial por campo.
 */
export function ScreenForm({
  fields,
  control,
  mode,
  resource,
  recordId,
}: ScreenFormProps) {
  return (
    <>
      {fields
        .filter((field) => {
          if (mode === 'create') return field.showOnCreate !== false;
          if (mode === 'edit') return field.showOnEdit !== false;
          return field.showOnView !== false;
        })
        .map((field) => {
          const disabled = mode === 'edit' && field.disabledOnEdit;

          const base = {
            name: field.name,
            control,
            label: field.label,
            required: field.required,
            sm: field.colSpan?.sm as any,
            md: field.colSpan?.md as any,
            lg: field.colSpan?.lg as any,
          };

          if (field.type === 'multi-relation' && field.relation?.resource) {
            if (!resource || !recordId) {
              return (
                <Field key={field.name} className="col-span-12">
                  <FieldLabel>{field.label}</FieldLabel>
                  <p className="text-sm text-muted-foreground">
                    Salve o registro primeiro pra configurar esta relação.
                  </p>
                </Field>
              );
            }

            return (
              <MultiRelationField
                key={field.name}
                resource={resource}
                recordId={recordId}
                label={field.label}
                relation={field.relation}
                readOnly={mode === 'view'}
              />
            );
          }

          if (field.type === 'component' && field.component) {
            const Component = fieldComponentRegistry[field.component];
            return Component ? (
              <Component key={field.name} {...base} disabled={disabled} />
            ) : null;
          }

          if (field.mask && field.mask.length > 0) {
            return (
              <FormMaskedInput
                key={field.name}
                {...base}
                mask={field.mask}
                disabled={disabled}
              />
            );
          }

          if (field.type === 'boolean') return <FormSwitch key={field.name} {...base} />;
          if (field.type === 'enum') {
            return (
              <FormSelect key={field.name} {...base} options={field.options ?? []} />
            );
          }
          if (field.type === 'relation' && field.relation?.resource) {
            return <RelationField key={field.name} {...base} relation={field.relation} />;
          }
          if (field.type === 'currency') {
            return <FormCurrencyInput key={field.name} {...base} disabled={disabled} />;
          }
          if (field.type === 'date') {
            return (
              <FormInput key={field.name} {...base} type="date" disabled={disabled} />
            );
          }
          if (field.type === 'datetime') {
            return (
              <FormInput
                key={field.name}
                {...base}
                type="datetime-local"
                disabled={disabled}
              />
            );
          }
          if (field.type === 'number') {
            return (
              <FormInput key={field.name} {...base} type="number" disabled={disabled} />
            );
          }
          if (field.type === 'password') {
            return (
              <FormInput key={field.name} {...base} type="password" disabled={disabled} />
            );
          }
          if (field.type === 'file') {
            return (
              <FormUploader
                key={field.name}
                {...base}
                accept={field.accept}
                disabled={disabled}
              />
            );
          }

          return <FormInput key={field.name} {...base} disabled={disabled} />;
        })}
    </>
  );
}

function RelationField({
  relation,
  ...props
}: {
  name: string;
  control: Control<FieldValues>;
  label: string;
  required?: boolean;
  sm?: any;
  md?: any;
  lg?: any;
  relation: RelationRef;
}) {
  return (
    <FormAsyncSelect
      {...props}
      useQuery={makeRelationSearchHook(relation.resource)}
      getOptionValue={(item: any) => item.id}
      getDisplayValue={(item: any) => item[relation.displayField]}
      clearable
    />
  );
}

/**
 * Campo de relação N:N — não passa pelo react-hook-form (a associação não é um campo do
 * recurso, é uma rota aninhada à parte). Cada mudança já salva na hora via PUT.
 */
function MultiRelationField({
  resource,
  recordId,
  label,
  relation,
  readOnly,
}: {
  resource: string;
  recordId: string;
  label: string;
  relation: RelationRef;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['nested-relation', resource, recordId, relation.resource];

  const { data: current } = useNestedRelation(resource, recordId, relation.resource);
  const { data: options } = makeRelationSearchHook(relation.resource)();

  const mutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await (apiClient as any)
        [resource]({ id: recordId })
        [relation.resource].put({ ids });
    },
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<any[]>(queryKey);
      const nextItems = (options ?? []).filter((item: any) => ids.includes(item.id));
      queryClient.setQueryData(queryKey, nextItems);
      return { previous };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const selectedIds = (current ?? []).map((item) => item.id);

  return (
    <Field className="col-span-12">
      <FieldLabel>{label}</FieldLabel>
      <MultiSelect
        label={label}
        options={options ?? []}
        getOptionValue={(item: any) => item.id}
        getDisplayValue={(item: any) => item[relation.displayField]}
        value={selectedIds}
        onChange={(ids) => mutation.mutate(ids)}
        disabled={readOnly}
      />
    </Field>
  );
}
